import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";

export const CASA_FORTUNA_PREVIEW_URL = "https://ristorante-fortuna.preview.emergentagent.com/";

const PROJECTS = [
  {
    id: "casa-fortuna",
    kind: "Concept di restyling",
    client: "Ristorante Casa Fortuna",
    category: "Cicchetteria & cucina di pesce — Mestre, dal 1977",
    note: "Proposta non commissionata · si apre in una nuova scheda",
    points: ["Mobile-first", "Prenotazioni dirette", "Storia · Menù · Galleria"],
    href: CASA_FORTUNA_PREVIEW_URL,
    cta: "Apri l'anteprima del sito",
    img: "/casa-fortuna-preview.jpg",
    alt: "Anteprima del nuovo sito di Casa Fortuna realizzato da Nex Studio Design",
    featured: true,
  },
  {
    id: "demo",
    kind: "Prodotti live",
    client: "Prenotazioni Direct & Menù QR",
    category: "Gli strumenti che installiamo nei siti dei clienti",
    note: "Provali adesso, nessun dato salvato",
    points: ["Zero commissioni", "3 lingue", "Allergeni UE"],
    to: "/demo",
    cta: "Prova le demo",
    img: "https://images.unsplash.com/photo-1632778129004-f142ce499b3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Demo prenotazioni e menù digitale",
  },
];

function ProjectLink({ item, className, children, testid }) {
  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" className={className} data-testid={testid} aria-label={item.cta}>
        {children}
      </a>
    );
  }
  return (
    <Link to={item.to} className={className} data-testid={testid} aria-label={item.cta}>
      {children}
    </Link>
  );
}

function ProjectCard({ item, delay }) {
  return (
    <motion.article
      data-testid={`project-card-${item.id}`}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={`group relative flex flex-col overflow-hidden rounded-3xl border border-line bg-panel transition-colors duration-500 hover:border-terracotta/50 ${
        item.featured ? "lg:col-span-7" : "lg:col-span-5"
      }`}
    >
      <ProjectLink item={item} className="relative block overflow-hidden">
        <img
          src={item.img}
          alt={item.alt}
          loading="lazy"
          className="h-72 w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04] sm:h-96"
        />
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(to top, rgba(14,12,11,0.7), transparent 55%)" }} />
        <span className="absolute left-5 top-5 rounded-full border border-bone/20 bg-ink/60 px-3.5 py-1.5 font-accent text-[10px] font-semibold uppercase tracking-[0.22em] text-bone backdrop-blur-md">
          {item.kind}
        </span>
        <span className="absolute bottom-5 left-5 font-accent text-[10px] uppercase tracking-[0.2em] text-ochre">{item.note}</span>
      </ProjectLink>
      <div className="flex flex-1 flex-col p-6 lg:p-7">
        <h3 className="font-serif text-2xl tracking-tight text-bone">{item.client}</h3>
        <p className="mt-1 text-sm text-stone">{item.category}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {item.points.map((p) => (
            <span key={p} className="rounded-full border border-line px-3 py-1 text-xs text-stone">{p}</span>
          ))}
        </div>
        <ProjectLink
          item={item}
          testid={`project-link-${item.id}`}
          className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 font-accent text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a]"
        >
          {item.cta}
          {item.href ? (
            <ExternalLink size={14} />
          ) : (
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          )}
        </ProjectLink>
      </div>
    </motion.article>
  );
}

export default function Portfolio() {
  return (
    <section id="casi-studio" className="bg-panel py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading
            tag="Progetti & concept"
            title={
              <>
                Non ti raccontiamo numeri: <em className="italic text-terracotta">ti facciamo navigare.</em>
              </>
            }
            sub="Siamo uno studio nuovo e lo diciamo. Al posto di casi studio inventati trovi concept di restyling reali, costruiti su locali veri, e prodotti che puoi provare subito."
          />
          <Reveal delay={0.2}>
            <p className="max-w-xs text-sm text-faded">
              Il prossimo concept potrebbe essere il tuo locale: lo prepariamo gratis, prima di qualsiasi impegno.
            </p>
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {PROJECTS.map((p, i) => (
            <ProjectCard key={p.id} item={p} delay={i * 0.1} />
          ))}
        </div>
      </div>
    </section>
  );
}
